import { List, Set } from "immutable";
import { createSelector } from "reselect";
import { AnnotationRecord } from "common/records/Annotation";
import { ElevationRecord } from "common/records/Elevation";
import { isBlankLocation, typedOpeningKey } from "common/records/Opening";
import { getUrl } from "common/records/Image";
import { recordChanged } from "common/helpers/Record";
import { BlockedLocationItem } from "common/helpers/Measurement";
import { RootState } from "common/reducers";
import { currentMeasurement } from "common/selectors/measurement.common.selectors";
import type { IRsfDrawingAnnotation } from "common/components/RsfDrawing";

export interface AnnotationRow {
  elevationId: number;
  annotation: AnnotationRecord;
  // Derived label (`${abbr}${id}`) — annotations no longer store `text`; the label is computed
  // from the parent elevation's abbr + the annotation's 1-based id. See `annotationLabel`.
  label: string;
  // The annotation's index WITHIN its own elevation's `annotations.list`. The AnnotationTable binds
  // each editable cell to a single `<Form>` via the path `byId.<elevationId>.annotations.list.<listIndex>.<field>`,
  // so it needs the per-elevation index, not the flattened cross-elevation row position.
  listIndex: number;
}

export const rootKey = "elevations";

/**
 * Derives an annotation's display label live from its parent elevation's `abbr` + the annotation's
 * 1-based `id` (rsf-app parity: `${abbr}${id}`, e.g. abbr "R" + id 1 → "R1"). rsf-app never
 * persists the label text — occ-app stopped storing it too, so it is computed on read. When `id` is
 * absent (0/null on a record hydrated without one), falls back to the annotation's 1-based position
 * in the elevation's list so labels stay distinct.
 *
 * A plain function, not a `createSelector` — it's called once per annotation inside loops (the
 * row/canvas/save consumers), where reselect's size-1 cache would only thrash; this matches the
 * repo's record→display-string precedent (`getFullName`, `getUrl`).
 *
 * @param {ElevationRecord} elevation The owning elevation (provides `abbr` + list for the fallback)
 * @param {AnnotationRecord} annotation The annotation to label
 * @returns {string} The derived label — e.g. "R1", or "1" when the elevation has no abbr
 */
export const annotationLabel = (elevation: ElevationRecord, annotation: AnnotationRecord): string => {
  const id = annotation.id;
  let n: number;
  if (id && id > 0) {
    n = id;
  } else {
    const index = elevation.annotations.get("list").indexOf(annotation);
    n = index >= 0 ? index + 1 : 1;
  }
  return elevation.abbr ? `${elevation.abbr}${n}` : String(n);
};

/**
 * Gets the elevation for the given id, or `undefined` when the id isn't hydrated in `byId`.
 *
 * @param {RootState} state The RootState
 * @param {{ elevationId: number }} props The props
 * @returns {ElevationRecord | undefined} elevation, or undefined if not present
 */
export const elevation = (state: RootState, props: { elevationId: number }) =>
  state.getIn([rootKey, "byId", props.elevationId]) as ElevationRecord | undefined;

/**
 * Gets the last saved elevation for the given id, or `undefined` when no save snapshot exists.
 *
 * @param {RootState} state The RootState
 * @param {{ elevationId: number }} props The props
 * @returns {ElevationRecord | undefined} elevation, or undefined if not present
 */
export const lastSavedElevation = (state: RootState, props: { elevationId: number }) =>
  state.getIn([rootKey, "lastSavedById", props.elevationId]) as ElevationRecord | undefined;

/**
 * Gets the elevations for the given ids. Individual entries are `undefined` when the
 * corresponding id isn't hydrated yet.
 *
 * @param {RootState} state The RootState
 * @param {{ elevationIds: List<number> }} props The props
 * @returns {List<ElevationRecord | undefined>} elevations (entries may be undefined)
 */
export const elevations = (state: RootState, props: { elevationIds: List<number> }) => {
  return props.elevationIds.map((id: number) => elevation(state, { elevationId: id }));
};

/**
 * Gets the last saved elevations for the given ids. Individual entries are `undefined` when no
 * save snapshot exists for the corresponding id.
 *
 * @param {RootState} state The RootState
 * @param {{ elevationIds: List<number> }} props The props
 * @returns {List<ElevationRecord | undefined>} elevations (entries may be undefined)
 */
export const lastSavedElevations = (state: RootState, props: { elevationIds: List<number> }) => {
  return props.elevationIds.map((id: number) => lastSavedElevation(state, { elevationId: id }));
};

/**
 * Gets the elevations for the current measurement. Individual entries are `undefined` when the
 * corresponding id isn't hydrated yet.
 *
 * @param {RootState} state The RootState
 * @returns {List<ElevationRecord | undefined>} elevations (entries may be undefined)
 */
export const currentMeasurementElevations = createSelector(
  [currentMeasurement, (state: RootState) => state],
  (measurement, state) => {
    return elevations(state, { elevationIds: measurement?.get("elevation_ids") || List() });
  },
);

/**
 * Gets the last saved elevations for the current measurement. Individual entries are `undefined`
 * when no save snapshot exists for the corresponding id.
 *
 * @param {RootState} state The RootState
 * @returns {List<ElevationRecord | undefined>} elevations (entries may be undefined)
 */
export const currentMeasurementLastSavedElevations = createSelector(
  [currentMeasurement, (state: RootState) => state],
  (measurement, state) => {
    return lastSavedElevations(state, { elevationIds: measurement?.get("elevation_ids") || List() });
  },
);

/**
 * Flattens annotations across the current measurement's elevations into a single list. Each row
 * pairs the annotation with the id of its owning elevation so callers can dispatch updates back
 * to the right elevation slot.
 *
 * @param {RootState} state The RootState
 * @returns {List<AnnotationRow>} cross-elevation annotation rows in elevation-then-list order
 */
export const currentMeasurementAnnotations = createSelector([currentMeasurementElevations], (elevations) => {
  let rows = List<AnnotationRow>();
  elevations.forEach((elevation: ElevationRecord | undefined) => {
    if (!elevation) return;
    const list = elevation.annotations.get("list");
    list.forEach((annotation, listIndex) => {
      rows = rows.push({
        elevationId: elevation.id,
        annotation,
        label: annotationLabel(elevation, annotation),
        listIndex,
      });
    });
  });
  return rows;
});

/**
 * The set of typed opening keys (`<opening_type>:<opening_id>`, lowercased) that the current
 * measurement's annotations link to (via the persisted `annotation.opening_id`/`opening_type`).
 * Openings carry no persisted `annotation_id` — rsf-api neither permits nor serializes it — so this
 * derivation is the only reload-stable way to know an opening is Window-inator-managed (rsf-app
 * parity: `Elevation.associateOpenings` does the same client-side). Keying by type+id (not id alone)
 * prevents a window and a door sharing an id from cross-linking. The Openings table uses membership
 * here to render linked openings read-only.
 *
 * @param {RootState} state The RootState
 * @returns {Set<string>} typed keys of openings linked to an annotation
 */
export const annotationLinkedOpeningKeys = createSelector([currentMeasurementElevations], (elevations) => {
  let keys = Set<string>();
  elevations.forEach((elevation: ElevationRecord | undefined) => {
    if (!elevation) return;
    elevation.annotations.get("list").forEach((annotation) => {
      if (annotation.opening_id) keys = keys.add(typedOpeningKey(annotation.opening_type, annotation.opening_id));
    });
  });
  return keys;
});

/**
 * An elevation's display image URL for `RsfDrawing` — the original first, because annotation
 * coordinates are image-pixel-space (rsf-app authors/renders against the original); a resized
 * derivative would offset every callout. Empty string when the elevation has no source image.
 *
 * @param {ElevationRecord} elevation
 * @returns {string} the image URL, or ""
 */
export const elevationImageUrl = (elevation: ElevationRecord): string =>
  getUrl(elevation.src_image, "original") ||
  getUrl(elevation.src_image, "large") ||
  getUrl(elevation.src_image, "thumb") ||
  "";

/**
 * An elevation's annotations mapped to `RsfDrawing`'s input shape, with labels derived via
 * `annotationLabel`.
 *
 * @param {ElevationRecord} elevation
 * @returns {IRsfDrawingAnnotation[]}
 */
export const rsfDrawingAnnotations = (elevation: ElevationRecord): IRsfDrawingAnnotation[] =>
  (elevation.annotations.get("list") ?? List<AnnotationRecord>())
    .map((a) => ({ guid: a.guid, text: annotationLabel(elevation, a), x: a.x, y: a.y }))
    .toArray();

/**
 * Guids of the annotations (across the given elevations) linked to any of the given typed opening
 * keys — drives the Openings Wizard's elevation highlighting of selected openings. Derived from the
 * persisted `annotation.opening_id`/`opening_type` link, keyed by type+id (see
 * `annotationLinkedOpeningKeys`) so a window and a door sharing an id don't cross-highlight. A plain
 * function over already-selected elevations, called with transient wizard selection state.
 *
 * @param {List<ElevationRecord | undefined>} elevations the elevations to scan
 * @param {Set<string>} openingKeys the selected openings' typed keys
 * @returns {string[]} the matching annotation guids
 */
export const annotationGuidsForOpeningKeys = (
  elevations: List<ElevationRecord | undefined>,
  openingKeys: Set<string>,
): string[] => {
  const guids: string[] = [];
  elevations.forEach((elevation) => {
    if (!elevation) return;
    elevation.annotations.get("list").forEach((annotation) => {
      if (annotation.opening_id && openingKeys.has(typedOpeningKey(annotation.opening_type, annotation.opening_id))) {
        guids.push(annotation.guid);
      }
    });
  });
  return guids;
};

/**
 * The set of `rendered_image_id` values across the current measurement's elevations. A render is
 * an elevation's `rendered_image`, so this set is the deterministic signal for excluding rendered
 * overlays from the Step 1 source-image picker. `0` (the record default for an elevation with no
 * render yet) is excluded so it never masks a real job image with id 0.
 *
 * @param {RootState} state The RootState
 * @returns {Set<number>} the rendered_image_id values that should be hidden from the picker
 */
export const currentMeasurementRenderedImageIds = createSelector([currentMeasurementElevations], (elevations) => {
  let ids = Set<number>();
  elevations.forEach((elevation: ElevationRecord | undefined) => {
    if (!elevation) return;
    if (elevation.rendered_image_id > 0) ids = ids.add(elevation.rendered_image_id);
  });
  return ids;
});

/** An annotation whose blank Location blocks the Window-inator's Save. */
export interface BlockingAnnotation extends BlockedLocationItem {
  elevationId: number;
  guid: string;
}

const EMPTY_BLOCKING_ANNOTATIONS = List<BlockingAnnotation>();

/**
 * Checks if the elevations have changed on the current measurement.
 *
 * @param {RootState} state The RootState
 * @returns {boolean} True if the elevations have changed, false otherwise
 */
export const elevationsChanged = (state: RootState): boolean => {
  return recordChanged(currentMeasurementElevations(state), currentMeasurementLastSavedElevations(state));
};

/**
 * Elevations on the current measurement that need (re)rendering + persisting on Save: brand-new
 * placeholders (negative id) and any whose current record differs from its `lastSavedById`
 * snapshot. `$hasChanged` is NOT used — the reducer only sets it on removals; annotation edits
 * (UPDATE_ANNOTATIONS / MOVE / SET_MEASURING_METADATA) are detected via deep `.equals()` against
 * `lastSavedById`, the same contract `elevationsChanged` relies on.
 *
 * @param {RootState} state The RootState
 * @returns {List<ElevationRecord>} the changed elevations, in measurement order
 */
export const currentMeasurementChangedElevations = createSelector(
  [currentMeasurementElevations, currentMeasurementLastSavedElevations],
  (current, lastSaved): List<ElevationRecord> => {
    let changed = List<ElevationRecord>();
    current.forEach((elevation: ElevationRecord | undefined, idx: number) => {
      if (!elevation) return;
      const prior = lastSaved.get(idx) ?? null;
      if (elevation.id <= 0 || recordChanged(elevation, prior)) {
        changed = changed.push(elevation);
      }
    });
    return changed;
  },
);

/**
 * Annotations across all of the current measurement's elevations whose Location is still blank — the
 * Window-inator's save block.
 *
 * @param state
 * @returns {List<BlockingAnnotation>} the blocking annotations, in elevation-then-annotation order
 */
export const currentMeasurementBlockingAnnotations = createSelector(
  [currentMeasurementElevations],
  (elevations): List<BlockingAnnotation> => {
    let blocking = List<BlockingAnnotation>();
    elevations.forEach((elevation) => {
      if (!elevation) return;
      elevation.annotations.get("list").forEach((annotation) => {
        if (!isBlankLocation(annotation.location)) return;
        blocking = blocking.push({
          elevationId: elevation.id,
          guid: annotation.guid,
          kind: annotation.opening_type === "door" ? "door" : "window",
          label: annotationLabel(elevation, annotation),
        });
      });
    });
    return blocking.isEmpty() ? EMPTY_BLOCKING_ANNOTATIONS : blocking;
  },
);

/**
 * The guids of the annotations blocking a save.
 *
 * @param state
 * @returns {Set<string>} guids of annotations missing a Location
 */
export const blockingAnnotationGuids = createSelector([currentMeasurementBlockingAnnotations], (blocking) =>
  Set(blocking.map((annotation) => annotation.guid)),
);
